import { 
  EyeOutlined, 
  MailOutlined, 
  CalendarOutlined, 
  DollarOutlined,
  ShoppingOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { Button, TableColumnsType, Tooltip, Tag, Modal, Table, message, Divider } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

import { WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { useOrders, Order, OrderItem } from '@hooks';

// Status color mapping
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'orange',
    paid: 'green',
    processing: 'blue',
    shipped: 'cyan',
    delivered: 'green',
    cancelled: 'red',
  };
  return colors[status] || 'default';
};

export const OrdersManagementPage = () => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Fetch orders (enable fetching for this page)
  const { orders } = useOrders({ enableOrdersFetch: true });
  const ordersList = orders.data || [];
  const isLoading = orders.isLoading;

  // Open modal to view order details
  const openOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setDetailModalOpen(true);
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Table columns configuration for order items
  const itemColumns: TableColumnsType<OrderItem> = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string) => (
        <span className="font-orator text-white font-bold">{text}</span>
      ),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      render: (sku: string) => (
        <span className="font-orator text-zinc-300 font-semibold">{sku || '-'}</span>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (qty: number) => (
        <span className="font-orator text-white font-bold">{qty}</span>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price: number) => (
        <span className="font-orator text-white font-bold">
          {formatPrice(price)}
        </span>
      ),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      width: 120,
      render: (_, record) => (
        <span className="font-orator text-white font-bold">
          {formatPrice(record.price * record.quantity)}
        </span>
      ),
    },
  ];

  // Table columns configuration for orders
  const columns: TableColumnsType<Order> = [
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string) => (
        <span className="font-orator text-orangicide font-semibold">{text}</span>
      ),
    },
    {
      title: 'Customer Email',
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      render: (text: string) => (
        <span className="font-orator text-white">{text}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="font-orator uppercase">
          {status}
        </Tag>
      ),
    },
    {
      title: 'Items',
      dataIndex: 'items',
      key: 'itemCount',
      width: 80,
      render: (items: OrderItem[]) => (
        <span className="font-orator text-white">{items?.length || 0}</span>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (amount: number) => (
        <span className="font-orator text-orangicide font-bold text-lg">
          {formatPrice(amount)}
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => (
        <span className="font-orator text-white">
          {dayjs(date).format('MMM DD, YYYY HH:mm')}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Tooltip title="View details">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openOrderDetails(record);
            }}
            className="btn-orangicide !text-black"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="overflow-x-hidden">
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat w-full py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6">
            <div className="font-displayed !font-normal md:!text-lg lg:!text-5xl text-orangicide">
              ORDERS MANAGEMENT
            </div>
          </div>

          <p className="!text-white font-orator !text-xl tracking-wider mb-6">
            View and manage all customer orders
          </p>

          <WrapperPage isLoading={isLoading}>
            <GenericList<Order>
              columns={columns}
              dataSource={ordersList}
              onRow={(record) => ({
                onClick: () => openOrderDetails(record),
                className: 'cursor-pointer hover:bg-zinc-800',
              })}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
              }}
            />
          </WrapperPage>

          {/* Modal for order details */}
          <Modal
            title={
              <div className="flex items-center gap-3">
                <ShoppingOutlined className="text-orangicide text-2xl" />
                <span className="font-orator text-orangicide text-xl tracking-wider">
                  ORDER DETAILS
                </span>
              </div>
            }
            open={detailModalOpen}
            onCancel={() => setDetailModalOpen(false)}
            footer={null}
            width={1000}
            className="order-details-modal"
            styles={{
              mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.8)' },
              content: { 
                backgroundColor: '#09090b', 
                border: '1px solid #3f3f46', 
                borderRadius: '0px',
                padding: '0',
              },
              header: { 
                backgroundColor: '#18181b', 
                borderBottom: '2px solid #3f3f46',
                padding: '20px 24px',
                marginBottom: '0',
                borderRadius: '0px',
              },
              body: {
                padding: '24px',
                maxHeight: 'calc(90vh - 120px)',
                overflowY: 'auto',
              }
            }}
          >
            {selectedOrder && (
              <div className="space-y-6">
                {/* Order Number Card */}
                <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 border border-zinc-700 p-6 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-orator font-bold text-orangicide text-sm uppercase tracking-wider mb-2">
                        Order Number
                      </p>
                      <h2 className="font-displayed text-white text-3xl font-normal tracking-wide">
                        {selectedOrder.orderNumber}
                      </h2>
                    </div>
                    <Tooltip title="copy order number">
                      <Button
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(selectedOrder.orderNumber, 'Order number')}
                        className="btn-orangicide"
                        size="large"
                      />
                    </Tooltip>
                  </div>
                </div>

                {/* Two Column Layout for Key Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Customer Email Card */}
                  <div className="bg-zinc-900 border border-zinc-700 p-5 hover:border-zinc-600 transition-all">
                    <div className="flex items-start gap-3">
                      <div className="bg-zinc-800 p-2">
                        <MailOutlined className="text-orangicide text-xl" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-orator text-zinc-400 text-xs uppercase tracking-wider mb-1">
                          Customer Email
                        </p>
                        <p className="font-orator text-white text-sm break-all">
                          {selectedOrder.customerEmail}
                        </p>
                        <Tooltip title="copy email">
                          <Button
                            type="link"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(selectedOrder.customerEmail, 'Email')}
                            className="text-white !p-0 !h-auto mt-1 font-orator !font-normal text-xs"
                          >
                            copy
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>

                  {/* Status Card */}
                  <div className="bg-zinc-900 border border-zinc-700 p-5 hover:border-zinc-600 transition-all">
                    <div className="flex items-start gap-3">
                      <div className="bg-zinc-800 p-2">
                        <CheckCircleOutlined className="text-orangicide text-xl" />
                      </div>
                      <div className="flex-1">
                        <p className="font-orator text-zinc-400 text-xs uppercase tracking-wider mb-2">
                          Status
                        </p>
                        <Tag 
                          color={getStatusColor(selectedOrder.status)} 
                          className="font-orator uppercase text-base px-4 py-1"
                        >
                          {selectedOrder.status}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates and Amount Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Amount */}
                  <div className="bg-zinc-900 border-2 border-orangicide p-5 shadow-lg shadow-orangicide/10">
                    <div className="flex items-start gap-3">
                      <div className="bg-orangicide/10 p-2">
                        <DollarOutlined className="text-orangicide text-xl" />
                      </div>
                      <div className="flex-1">
                        <p className="font-orator text-zinc-400 text-xs uppercase tracking-wider mb-1">
                          Total Amount
                        </p>
                        <p className="font-displayed text-orangicide text-2xl font-bold">
                          {formatPrice(selectedOrder.totalAmount)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Created At */}
                  <div className="bg-zinc-900 border border-zinc-700 p-5">
                    <div className="flex items-start gap-3">
                      <div className="bg-zinc-800 p-2">
                        <CalendarOutlined className="text-orangicide text-xl" />
                      </div>
                      <div className="flex-1">
                        <p className="font-orator text-zinc-400 text-xs uppercase tracking-wider mb-1">
                          Created At
                        </p>
                        <p className="font-orator text-white text-xs">
                          {dayjs(selectedOrder.createdAt).format('MMM DD, YYYY')}
                        </p>
                        <p className="font-orator text-zinc-500 text-xs mt-0.5">
                          {dayjs(selectedOrder.createdAt).format('HH:mm:ss')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Updated At */}
                  <div className="bg-zinc-900 border border-zinc-700 p-5">
                    <div className="flex items-start gap-3">
                      <div className="bg-zinc-800 p-2">
                        <ClockCircleOutlined className="text-orangicide text-xl" />
                      </div>
                      <div className="flex-1">
                        <p className="font-orator text-zinc-400 text-xs uppercase tracking-wider mb-1">
                          Updated At
                        </p>
                        <p className="font-orator text-white text-xs">
                          {dayjs(selectedOrder.updatedAt).format('MMM DD, YYYY')}
                        </p>
                        <p className="font-orator text-zinc-500 text-xs mt-0.5">
                          {dayjs(selectedOrder.updatedAt).format('HH:mm:ss')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <Divider className="!bg-zinc-700 !my-6" />

                {/* Order Items Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <ShoppingOutlined className="text-orangicide text-xl" />
                    <h3 className="font-orator font-bold text-orangicide text-lg uppercase tracking-wider">
                      Order Items
                    </h3>
                    {selectedOrder.items && selectedOrder.items.length > 0 && (
                      <span className="bg-orangicide text-black font-orator text-xs px-2 py-1 ml-2">
                        {selectedOrder.items.length} {selectedOrder.items.length === 1 ? 'item' : 'items'}
                      </span>
                    )}
                  </div>
                  
                  <div className="bg-zinc-900 border border-zinc-700 overflow-hidden">
                    <Table
                      columns={itemColumns}
                      dataSource={selectedOrder.items}
                      rowKey="id"
                      pagination={false}
                      size="middle"
                      className="order-items-table"
                      rowClassName="bg-zinc-900 hover:bg-zinc-800 transition-colors"
                      locale={{
                        emptyText: (
                          <div className="py-12">
                            <div className="flex flex-col items-center justify-center">
                              <div className="bg-zinc-800 p-6 mb-4">
                                <ShoppingOutlined className="text-zinc-600 text-5xl" />
                              </div>
                              <p className="font-orator text-zinc-400 text-lg mb-2">No items in this order</p>
                              <p className="font-orator text-zinc-600 text-sm">Order items will appear here</p>
                            </div>
                          </div>
                        )
                      }}
                    />
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-end pt-4 border-t border-zinc-700">
                  <Button
                    type="primary"
                    key="close"
                    onClick={() => setDetailModalOpen(false)}
                    size="large"
                    className="btn-orangicide font-orator font-bold px-8 uppercase tracking-wider"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </section>
    </div>
  );
};

